/**
 * Turns API / HTTP error payloads into a user-facing toast detail.
 * Backend messages are "<code>:<message>"; Angular HTTP failures are never shown.
 */

const TECHNICAL_HTTP_MESSAGE = /^http failure response/i;
const URL_OR_API_PATH = /https?:\/\/|^\/\/|\/v\d+\.\d+\//i;
const HTTP_STATUS_DUMP = /^\d{3}\s+\w+/;
const HTML_ERROR_PAGE = /<!doctype|<html/i;

export function toUserFacingErrorDetail(errors?: unknown[], rawMessage?: unknown): string | null {
  if (Array.isArray(errors) && errors.length > 0) {
    const messages = errors
      .map((err) => messageFromUnknownError(err))
      .filter((message): message is string => !!message);

    if (messages.length > 0) {
      return messages.join(', ');
    }
  }

  return messageFromUnknownError(rawMessage);
}

function messageFromUnknownError(err: unknown): string | null {
  const raw = rawErrorText(err);
  if (!raw || isTechnicalHttpMessage(raw)) {
    return null;
  }

  const colonIndex = raw.indexOf(':');
  const extracted = (colonIndex >= 0 ? raw.slice(colonIndex + 1) : raw).trim();
  if (!extracted || isTechnicalHttpMessage(extracted)) {
    return null;
  }

  return extracted;
}

function rawErrorText(err: unknown): string {
  if (typeof err === 'string') {
    return err.trim();
  }

  if (err && typeof err === 'object') {
    const candidate = (err as { message?: unknown; Message?: unknown }).message
      ?? (err as { Message?: unknown }).Message;
    if (typeof candidate === 'string') {
      return candidate.trim();
    }
  }

  return '';
}

function isTechnicalHttpMessage(value: string): boolean {
  return (
    TECHNICAL_HTTP_MESSAGE.test(value) ||
    URL_OR_API_PATH.test(value) ||
    HTTP_STATUS_DUMP.test(value) ||
    HTML_ERROR_PAGE.test(value)
  );
}
